<template>
    <div class="row items-center justify-end">
        <q-btn-group flat square v-if="!$q.platform.is.mobile && hasValidData">
            <q-btn
                id="download-csv-button"
                @click="exportDataAsCSV"
                :loading="isDownloadingCSV"
                icon="far fa-file-csv"
                color="primary"
                class="q-mr-sm"
                outline
                :label="$t('Export all data (CSV)')"
            >
                <q-tooltip style="font-size: 14px">
                    {{ $t('Export all data (CSV)') }}
                </q-tooltip>
            </q-btn>
            <q-btn
                id="download-xls-button"
                @click="exportDataAsXLS"
                :loading="isDownloadingXLS"
                icon="far fa-file-excel"
                color="primary"
                outline
                :label="$t('Export all data (XLS)')"
            >
                <q-tooltip style="font-size: 14px">
                    {{ $t('Export all data (XLS)') }}
                </q-tooltip>
            </q-btn>
        </q-btn-group>
    </div>
    <div
        class="row items-center justify-start q-pa-md bg-white q-my-lg border-grey"
        v-if="!hasValidData"
    >
        <q-icon
            name="fa-regular fa-exclamation-triangle"
            size="20px"
            class="q-mr-md"
        />
        {{ $t('No records found for this date range.') }}
    </div>
    <div v-else>
        <benchmark-oee-chart
            v-if="oeeChartData"
            :chart-data="oeeChartData"
            :facilities="facilities"
        />
        <benchmark-performance-chart
            v-if="performanceChartData"
            :chart-data="performanceChartData"
            :facilities="facilities"
        />
    </div>
</template>

<script>
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import BenchmarkPerformanceChart from './BenchmarkPerformanceChart.vue';
import BenchmarkOeeChart from './BenchmarkOeeChart.vue';
import { BenchmarkReportBuilder } from './BenchmarkReportBuilder.js';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.ts';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'BenchmarkReport',
    components: {
        BenchmarkPerformanceChart,
        BenchmarkOeeChart
    },
    props: {
        records: {
            type: Array,
            required: true,
            validator(val) {
                return Array.isArray(val) && val.length > 0;
            }
        },
        selectedGroups: {
            type: Array,
            required: true,
            validator(val) {
                return (
                    Array.isArray(val) &&
                    val.length > 0 &&
                    val.every(group => {
                        return (
                            typeof group === 'object' &&
                            'label' in group &&
                            'value' in group &&
                            (Array.isArray(group.value) ||
                                typeof group.value === 'number')
                        );
                    })
                );
            }
        }
    },
    created() {
        // initialize file exporter instance
        this.fileExporter = new FileExporter({
            headers: this.fileHeaders
        });
    },
    mounted() {
        // create report data
        this.createReportData();
    },
    data() {
        return {
            isLoading: false,
            facilities: [],
            oeeChartData: {
                oee: [],
                performance: [],
                availability: [],
                quality: []
            },
            performanceChartData: {
                running: [],
                planned: [],
                unplanned: []
            },
            // button loading animation
            isDownloadingCSV: false,
            isDownloadingXLS: false,

            // file exporter class
            fileExporter: null,
            fileRows: [],
            recordsData: this.records || []
        };
    },
    computed: {
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange',
            selectedTimezone: 'selectedTimezone'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for exporting
         */
        fileHeaders() {
            return [
                this.$t('Facility'),
                this.$t('Total Gross Production (products)'),
                this.$t('Total Reject Production (products)'),
                this.$t('Total Net Production (products)'),
                this.$t('Total Expected Production (products)'),
                this.$t('Total Running Duration [min]'),
                this.$t('Total Performance Downtime [min]'),
                this.$t('Total Availability Downtime [min]'),
                this.$t('Total Planned Downtime [min]'),
                this.$t('Total Changeover Duration [min]'),
                this.$t('Total Downtime [min]'),
                this.$t('Total Unplanned Downtime [min]'),
                this.$t('Availability [%]'),
                this.$t('Performance [%]'),
                this.$t('Quality [%]'),
                this.$t('OEE [%]'),
                this.$t('Report Start Date').concat(
                    ` [${this.selectedTimezone}]`
                ),
                this.$t('Report End Date').concat(` [${this.selectedTimezone}]`)
            ];
        },
        /**
         * Get selected facility and group labels
         * @return {String} facility and group labels, separated by underscore
         */
        facilityAndGroupLabel() {
            const labels = this.selectedGroups.map(group => group.label);
            labels.sort((a, b) => a.localeCompare(b));
            return labels.join('_');
        },
        startDate() {
            return this.formatDate(this.getDateRange.start);
        },
        endDate() {
            return this.formatDate(this.getDateRange.end);
        },
        /**
         * Checks if there is valid data in the chart.
         * @returns {boolean} True if there is valid data.
         */
        hasValidData() {
            if (!this.oeeChartData || !this.performanceChartData) {
                return false;
            }
            const chartDataArrays = [
                this.oeeChartData.oee,
                this.oeeChartData.performance,
                this.oeeChartData.availability,
                this.oeeChartData.quality,
                this.performanceChartData.running,
                this.performanceChartData.planned,
                this.performanceChartData.unplanned
            ];

            return !chartDataArrays.every(array =>
                array.every(value => value === 0)
            );
        }
    },
    methods: {
        /**
         * Create report data
         * @return {Object} report data containing OEE and performance charts data
         */
        createReportData() {
            this.isLoading = true;
            try {
                // initialize benchmark report builder
                const benchmarkReportBuilder = new BenchmarkReportBuilder();
                const reportData = benchmarkReportBuilder.build(
                    this.recordsData
                );
                this.facilities = reportData.facilities;
                this.oeeChartData = reportData.oeeChart;
                this.performanceChartData = reportData.performanceChart;

                // Create file rows for export
                this.fileRows = benchmarkReportBuilder.buildFileRows(
                    this.recordsData,
                    this.startDate,
                    this.endDate
                );
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'There was an error generating reports. Please try again.'
                    ),
                    error
                );
            } finally {
                this.isLoading = false;
            }
        },
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Export raw records as CSV.
         */
        async exportDataAsCSV() {
            // begin loading
            this.isDownloadingCSV = true;

            try {
                await this.fileExporter.exportCSV({
                    filename: `${this.facilityAndGroupLabel}_benchmark_${this.startDate}_${this.endDate}`,
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingCSV = false;
        },
        /**
         * Export raw records as XLS.
         */
        exportDataAsXLS() {
            // begin loading
            this.isDownloadingXLS = true;
            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityAndGroupLabel}_benchmark_${this.startDate}_${this.endDate}`,
                    sheetName: this.$t('Benchmark Report'),
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    ),
                    error
                );
            }
            // end loading
            this.isDownloadingXLS = false;
        }
    }
};
</script>
<style lang="scss" scoped></style>
